import { FC, useMemo, ReactElement, useState, useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import size from 'lodash/size'

import { schemaNumber } from 'schemas/baseSchema'
import { CryptoRewardsPanePreset } from 'components/features/rewards/rewardsPane/components/CryptoRewardsPanePreset/CryptoRewardsPanePreset'
import { CryptoRewardsPaneSumPicker } from 'components/features/rewards/rewardsPane/components/CryptoRewardsPaneSumPicker/CryptoRewardsPaneSumPicker'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { CRYPTO_REWARDS_SELL_MODE_STEP_SELL_AMOUNT } from 'components/features/rewards/rewardsPane/constants/paneSteps'
import { useGetCryptoCurrencyNameById } from 'components/features/rewards/rewardsPane/hooks/useGetCryptoCurrencyNameById'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { CryptoRewardsSellModeDataFields, CryptoRewardsSellModeFields, CryptoRewardsSellModeSteps } from 'components/features/rewards/rewardsPane/types/paneStateSellMode'
import {
  getCryptoRewardsPaneFieldsStateSellModeOrNullSelector,
  getCryptoRewardsPaneProgressPercentsStateSelector,
  getCryptoRewardsPaneStateSellModeOrNullSelector,
} from 'components/features/rewards/rewardsPane/store/selectors/paneState'
import { useSimpleGuard } from 'helpers/loadableHelpers'
import { rewardsSummaryGuard } from 'components/features/rewards/rewardsPane/guards/rewardsSummaryGuard'
import { cryptoMetaGuard } from 'components/features/rewards/rewardsPane/guards/cryptoMetaGuard'
import { getSellOrderFailedResultOrNullSelector, getSellPreviewOrderFailedResultOrNullSelector } from 'components/features/rewards/rewardsPane/store/selectors/orderState'
import { useServerErrorsApplyToForm } from 'hooks/useServerErrorsApplyToForm'
import { GeneralErrors } from 'interfaces/errors'
import { PreviewOrderFailedResult } from 'components/features/rewards/rewardsPane/types/orderPreviewResult'
import { OrderResultsReducerResetLastPreviewOrderErrorsAction } from 'components/features/rewards/rewardsPane/store/actions/ordersResults'
import { SuccessCryptoRewardsPaneSellModeStepAction } from 'components/features/rewards/rewardsPane/store/actions/paneState'
import { CryptoCurrencyId } from 'components/features/rewards/rewardsPane/types/cryptoCurrency'
import resolveErrorById, { NONZERO_CURRENCY_INVALID, TYPE_REQUIRED } from 'constants/errors'

import styles from 'components/features/rewards/rewardsPane/containers/CryptoRewardsSellPane/CryptoRewardsSellModeSellAmountStep/CryptoRewardsSellModeSellAmountStep.styl'
import { useCryptoRewardsPaneModifyStateCallback } from 'components/features/rewards/rewardsPane/hooks/useCryptoRewardsPaneModifyFieldsStateCallback'
import { FLOATING_PANE_CRYPTO_REWARDS_SELL_MODE } from 'components/features/rewards/rewardsPane/constants/paneState'
import { useBalance, useMinSellCryptoAmount, useSellAmountCrypto } from 'components/features/rewards/rewardPage/useBalance'
import Button from 'components/features/pfm/Button/Button'
import { FormPreset } from 'components/atoms/inputs/FormPreset/FormPreset'


export type CryptoSellFields = {
  amount_crypto: string
  amount_usd: string
  ticker: string
}

type CryptoRewardsSellModeSellAmountStepProps = {
  onClose: () => void
}

type CryptoRewardsSellModeSellAmountStepReadyProps = CryptoRewardsSellModeSellAmountStepProps & {
  cryptoCurrencyIdForSelling: CryptoCurrencyId
  sellOrderErrorsOrNull: PreviewOrderFailedResult | null
  cancelButtonName: string
  onGoBack: () => void
}

const STEP_TITLE = 'Sell Your Crypto Investments'

const STEP_SUBTITLE = 'How much would you like to sell?'

const FIELD_INPUT_SUM_NAME = 'amount_usd'

export const CryptoRewardsSellModeSellAmountStepReady: FC<CryptoRewardsSellModeSellAmountStepReadyProps> = ({
  cryptoCurrencyIdForSelling,
  sellOrderErrorsOrNull,
  cancelButtonName,
  onClose,
  onGoBack,
}) => {
  const dispatch = useStoreDispatch()
  const [formGeneralErrorsOrNull, setFormGeneralErrorsOrNull] = useState<GeneralErrors | null>(null)
  const formGeneralErrorToRender: [string] | null = useMemo(() => formGeneralErrorsOrNull ? [formGeneralErrorsOrNull[0]] : null, [formGeneralErrorsOrNull])

  const paneProgressPercent = useStoreSelector(getCryptoRewardsPaneProgressPercentsStateSelector)
  const cryptoCurrencyName: string = useGetCryptoCurrencyNameById(cryptoCurrencyIdForSelling)
  const currencyAmountUSD = useBalance(cryptoCurrencyIdForSelling) || '0'
  const minSellAmount = useMinSellCryptoAmount(cryptoCurrencyIdForSelling) || '0'
  const sellOrderFieldsErrorsOrNull = sellOrderErrorsOrNull?.fieldsErrors ?? null

  const formValidationSchemaYup = useMemo(() => (
    yup.object({
      [FIELD_INPUT_SUM_NAME]: schemaNumber
        .positive(resolveErrorById(NONZERO_CURRENCY_INVALID))
        .required(resolveErrorById(TYPE_REQUIRED))
        .min(+minSellAmount, `Minimum sell limit is ${ formatUSCurrency(minSellAmount, 'penny') }`)
        .max(+currencyAmountUSD, `Only ${ formatUSCurrency(currencyAmountUSD, 'penny') } is available`),
    })
  ), [currencyAmountUSD, minSellAmount])

  const formContext = useForm<CryptoSellFields>({
    resolver: yupResolver(formValidationSchemaYup),
    defaultValues: {
      [FIELD_INPUT_SUM_NAME]: currencyAmountUSD,
    },
  })
  useServerErrorsApplyToForm(formContext, sellOrderFieldsErrorsOrNull ?? {})

  const {
    watch,
    clearErrors: clearFormErrors,
    handleSubmit,
    formState: {
      isSubmitting,
      errors: formFieldsContextErrors,
    },
  } = formContext

  const sellAmountUSD: string = watch(FIELD_INPUT_SUM_NAME)
  const sellAmountCrypto = useSellAmountCrypto(+sellAmountUSD || 0, cryptoCurrencyIdForSelling)

  const sumPickerLabel = useMemo<string>(() => (
    `Available ${ cryptoCurrencyName }: ${ formatUSCurrency(currencyAmountUSD, 'penny') }`
  ), [cryptoCurrencyName, currencyAmountUSD])
  const sumPicker = useMemo<ReactElement>(() => (
    <CryptoRewardsPaneSumPicker className={ styles.sumPicker } name={ FIELD_INPUT_SUM_NAME } note={ sumPickerLabel } />
  ), [sumPickerLabel])

  const updatedPaneFieldsState = useMemo<Required<CryptoRewardsSellModeDataFields>>(() => ({
    cryptoCurrencyIDChosenForSelling: cryptoCurrencyIdForSelling,
    sellingAmountUSD: `${ (+sellAmountUSD || 0).toFixed(2) }`,
    sellingAmountCrypto: sellAmountCrypto || '0',
  }), [sellAmountUSD, cryptoCurrencyIdForSelling, sellAmountCrypto])

  const handleOpenOrderPreviewStep = useMemo(() => handleSubmit(() => {
    dispatch(new SuccessCryptoRewardsPaneSellModeStepAction({
      step: CRYPTO_REWARDS_SELL_MODE_STEP_SELL_AMOUNT,
      fields: {
        errors: [],
        ...updatedPaneFieldsState,
      },
      backStep: CRYPTO_REWARDS_SELL_MODE_STEP_SELL_AMOUNT,
    }))
  }), [handleSubmit, dispatch, updatedPaneFieldsState])

  const handleErrorsLayoutClosed = useCallback(() => {
    setFormGeneralErrorsOrNull(null)
  }, [setFormGeneralErrorsOrNull])

  useEffect(() => {
    if (sellOrderErrorsOrNull) {
      const { generalErrors, fieldsErrors } = sellOrderErrorsOrNull
      if (generalErrors?.length) {
        setFormGeneralErrorsOrNull(generalErrors)
      } else if (fieldsErrors?.ticker?.length) {
        setFormGeneralErrorsOrNull(fieldsErrors.ticker)
      }
      clearFormErrors('ticker')
      dispatch(new OrderResultsReducerResetLastPreviewOrderErrorsAction())
    }
  }, [sellOrderErrorsOrNull, clearFormErrors, dispatch])

  const isNextStepButtonDisabled = Boolean(size(formFieldsContextErrors))

  return (
    <CryptoRewardsPanePreset
      isLoading={ isSubmitting }
      title={ STEP_TITLE }
      subTitle={ STEP_SUBTITLE }
      cancelName={ cancelButtonName }
      progressBarPercent={ paneProgressPercent }
      actionDisabled={ isNextStepButtonDisabled }
      errors={ formGeneralErrorToRender }
      onCancel={ onClose }
      onFooterCancel={ onGoBack }
      onErrorsOverlayClosed={ handleErrorsLayoutClosed }
    >
      <FormPreset formContext={ formContext } onSubmit={ handleOpenOrderPreviewStep }>
        { sumPicker }
        <Button text='Preview Sale' submit fullWidth />
      </FormPreset>
    </CryptoRewardsPanePreset>
  )
}

export const CryptoRewardsSellModeSellAmountStep: FC<CryptoRewardsSellModeSellAmountStepProps> = ({ onClose }) => {
  const isCryptoWalletLoaded = useSimpleGuard(
    rewardsSummaryGuard,
    {},
    'CryptoRewardsSellCryptoWalletStep'
  )
  const isCryptoMetaLoaded = useSimpleGuard(
    cryptoMetaGuard,
    {},
    'CryptoRewardsSellCryptoWalletStep'
  )

  const paneProgressPercent = useStoreSelector(getCryptoRewardsPaneProgressPercentsStateSelector)
  const paneFieldsStateOrNull: Partial<CryptoRewardsSellModeFields> | null = useStoreSelector(getCryptoRewardsPaneFieldsStateSellModeOrNullSelector)
  const sellPreviewOrderErrorsOrNull: PreviewOrderFailedResult | null = useStoreSelector(getSellPreviewOrderFailedResultOrNullSelector)
  const sellOrderErrorsOrNull: PreviewOrderFailedResult | null = useStoreSelector(getSellOrderFailedResultOrNullSelector)
  const orderErrorsToRender = sellOrderErrorsOrNull ?? sellPreviewOrderErrorsOrNull

  const cryptoCurrencyIdForSelling = paneFieldsStateOrNull?.cryptoCurrencyIDChosenForSelling ?? null
  const isStepRequiredDataLoading = !isCryptoWalletLoaded || !isCryptoMetaLoaded

  const sellPaneStateOrNull: CryptoRewardsSellModeSteps | null = useStoreSelector(getCryptoRewardsPaneStateSellModeOrNullSelector)
  const modifyPaneState = useCryptoRewardsPaneModifyStateCallback(FLOATING_PANE_CRYPTO_REWARDS_SELL_MODE)
  const backStep = sellPaneStateOrNull?.backStep
  const handleCancelPressed = useCallback((): void => {
    if (backStep) {
      modifyPaneState({
        step: backStep,
        backStep: CRYPTO_REWARDS_SELL_MODE_STEP_SELL_AMOUNT,
      })
      return
    }
    onClose()
  }, [backStep, onClose, modifyPaneState])

  const stepNode = useMemo<ReactElement>(() => {
    const backButtonLabel = backStep ? 'Back' : 'Cancel'

    if (isStepRequiredDataLoading || !cryptoCurrencyIdForSelling) {
      return (
        <CryptoRewardsPanePreset
          isLoading
          title={ STEP_TITLE }
          subTitle={ STEP_SUBTITLE }
          progressBarPercent={ paneProgressPercent }
          cancelName={ backButtonLabel }
          onCancel={ onClose }
          onFooterCancel={ handleCancelPressed }
        />
      )
    }
    return (
      <CryptoRewardsSellModeSellAmountStepReady
        cryptoCurrencyIdForSelling={ cryptoCurrencyIdForSelling }
        sellOrderErrorsOrNull={ orderErrorsToRender }
        cancelButtonName={ backButtonLabel }
        onClose={ onClose }
        onGoBack={ handleCancelPressed }
      />
    )
  },
  [isStepRequiredDataLoading, paneProgressPercent, cryptoCurrencyIdForSelling, orderErrorsToRender, backStep, onClose, handleCancelPressed])

  return stepNode
}
